import { Box, styled, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import ClientLogo from 'components/ClientLogo'
import { useForgotPasswordMutation } from '@client-hub/store/api/crs/react'
import { FloatingInput, Button, Loader } from '@icanbwell/ui-toolkit'
import loginBackground from 'assets/login_background.png'
import Link from '@client-hub/shared/react/components/Link'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'

const useChangePasswordSchema = () => {
  const { t } = useTranslation()
  return Yup.object({
    email: Yup.string().email(t('forms.validations.invalidEmail')).required(t('forms.validations.requiredEmail')),
  }).required()
}
export type CredentialsData = Yup.InferType<ReturnType<typeof useChangePasswordSchema>>

const Wrapper = styled(Box)({
  background: `linear-gradient(0deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${loginBackground}) no-repeat fixed center`,
  minHeight: '100vh',
  backgroundSize: 'cover',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
})

const ForgotPassword = () => {
  const { t } = useTranslation()
  const schema = useChangePasswordSchema()
  const [isResetMailSent, setResetMailSent] = useState(false)
  const [forgotPassword, { isLoading: forgotPasswordLoading, isSuccess: forgotPasswordSuccess }] =
    useForgotPasswordMutation()
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CredentialsData>({
    resolver: yupResolver(schema),
  })

  useEffect(() => {
    if (forgotPasswordSuccess) {
      setResetMailSent(true)
    }
  }, [forgotPasswordSuccess])

  return (
    <Wrapper pt={6}>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight={500}
        maxWidth={350}
      >
        {forgotPasswordLoading ? (
          <Loader />
        ) : (
          <>
            <ClientLogo style={{ maxHeight: '10em' }} width="20em" />
            <Box color="white" fontSize="1.5em" textAlign="center" my={3}>
              {t('routes.forgotPassword.title')}
            </Box>
            <Box sx={{ color: 'white', lineHeight: '20px' }} mb={3} textAlign="center">
              {isResetMailSent ? t('routes.forgotPassword.resetLinkSent') : t('routes.forgotPassword.sendResetLink')}
            </Box>
            {!isResetMailSent && (
              <>
                <Box
                  sx={{ '& > div': { mb: 2 }, width: '20em' }}
                  component="form"
                  autoComplete="off"
                  display="flex"
                  flexDirection="column"
                  onSubmit={handleSubmit(forgotPassword)}
                >
                  <FloatingInput
                    id="credentials-email"
                    label={t('forms.credentials.username')}
                    {...register('email')}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />

                  <Button
                    sx={{ mt: 3 }}
                    width="100%"
                    id="credentials-submit-button"
                    type="submit"
                    text={t('forms.credentials.sendEmail')}
                    disabled={isSubmitting}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: '15px' }}>
                  <Link to="/login">
                    <Typography sx={{ color: 'white' }}>{t('register.backToSignIn')}</Typography>
                  </Link>
                </Box>
              </>
            )}
            {isResetMailSent && (
              <Link to="/login">
                <Typography sx={{ color: 'white', fontSize: '1.2rem' }}>{t('register.returnToSignIn')}</Typography>
              </Link>
            )}
          </>
        )}
      </Box>
    </Wrapper>
  )
}

export default ForgotPassword
